import { IEvent } from '@/types'

export const EVENT_STATUS = {
  LIVE: 'live',
  FINISHED: 'finished',
  FUTURE: 'future',
  PROGRAMMED: 'programmed',
  UNKNOWN: 'unknown',
} as const

export type EventStatusKey = typeof EVENT_STATUS[keyof typeof EVENT_STATUS]

export type EventUiState = {
  status: EventStatusKey
  hasVideos: boolean
  isLive: boolean
  isFinished: boolean
  isUpcoming: boolean
  isUpcomingWithoutVideos: boolean
  hasReplay: boolean
  hasPlaceholderVideo: boolean
  canWatchNow: boolean
  canWatchSoon: boolean
  shouldShowNoVideosMessage: boolean
}

const normalizeStatus = (event?: IEvent | null): EventStatusKey => {
  const status = event?.programmingData?.status

  if (status === EVENT_STATUS.LIVE) return EVENT_STATUS.LIVE
  if (status === EVENT_STATUS.FINISHED) return EVENT_STATUS.FINISHED
  if (status === EVENT_STATUS.FUTURE) return EVENT_STATUS.FUTURE
  if (status === EVENT_STATUS.PROGRAMMED) return EVENT_STATUS.PROGRAMMED

  return EVENT_STATUS.UNKNOWN
}

export const getEventUiState = (event?: IEvent | null): EventUiState => {
  const status = normalizeStatus(event)
  const hasVideos = (event?.videos?.length ?? 0) > 0

  // Some surfaces consume eventStatus.category instead of programmingData.status.
  const isLiveByCategory = event?.eventStatus?.category === EVENT_STATUS.LIVE
  const isLive = status === EVENT_STATUS.LIVE || isLiveByCategory

  const isFinished = status === EVENT_STATUS.FINISHED
  const isUpcoming = status === EVENT_STATUS.FUTURE || status === EVENT_STATUS.PROGRAMMED
  const isUpcomingWithoutVideos = isUpcoming && !hasVideos

  const hasReplay = isFinished && hasVideos
  const hasPlaceholderVideo = status === EVENT_STATUS.PROGRAMMED && hasVideos

  const canWatchNow = isLive || hasReplay || hasPlaceholderVideo
  const canWatchSoon = isUpcomingWithoutVideos

  return {
    status,
    hasVideos,
    isLive,
    isFinished,
    isUpcoming,
    isUpcomingWithoutVideos,
    hasReplay,
    hasPlaceholderVideo,
    canWatchNow,
    canWatchSoon,
    shouldShowNoVideosMessage: isFinished && !hasVideos,
  }
}
